import { create } from 'zustand';
import {
  onboardingFlows,
  type FlowVariantMap,
  type OnboardingAction,
  type OnboardingFlowId,
  type OnboardingVariant,
} from '@config/onboarding-flows';
import { isCompleted } from '@utils/onboardingProgress';
import { createGrantFlow, type GrantFlow } from '@lib/overlay/grantFlow';

/**
 * Onboarding's turn on the overlay mutex (overlay-system-design §6.3, §6.6). One constant id,
 * NOT the flow identity: the store is single-slot, so a re-entry with a new flow/variant/
 * contextId while a request is pending or held replaces the payload in place rather than
 * queuing a second request.
 */
export const ONBOARDING_GRANT_ID = 'onboarding';

// the state isn't as type safe as it could be, we could make it so a specific
// OnboardingFlowId variant is tied to its respective *Variant type
// but it gets tricky and at this point with just 1 variant it's pointless
export interface OnboardingFlowPayload {
  flowId: OnboardingFlowId;
  variant: string;
  actions: Partial<Record<OnboardingAction, () => void>>;
  contextId?: string;
}

interface OnboardingStoreState {
  /** Whether the drawer is on screen. Written only by the grant flow. */
  isOpen: boolean;
  /**
   * The pending or on-screen request's payload. Exists while a request is pending, not only
   * while it is held, so a re-entry mid-wait replaces it in place instead of queuing.
   */
  flow: OnboardingFlowPayload | null;
}

interface OnboardingStoreActions {
  openOnboarding: (params: {
    flowId: OnboardingFlowId;
    variant: string;
    actions?: Partial<Record<OnboardingAction, () => void>>;
    contextId?: string;
  }) => void;
  /** The host's Android back path for the held grant; null clears it. */
  setBackHandler: (fn: (() => void) | null) => void;
  closeOnboarding: (reason?: 'user' | 'programmatic') => void;
}

/** The host's Android back path for the held grant; set by GlobalOnboarding (§6.5). */
let backHandler: (() => void) | null = null;

let flow: GrantFlow;

export const useOnboardingStore = create<OnboardingStoreState & OnboardingStoreActions>(
  (set, get) => {
    flow = createGrantFlow(ONBOARDING_GRANT_ID, {
      onGranted: () => {
        set({ isOpen: true });
      },
      onClosed: () => {
        // 'pending': a newer request superseded this close and already rewrote the payload in
        // place above -- clear the open-state only, keep the newer payload.
        set(flow.getState() === 'idle' ? { isOpen: false, flow: null } : { isOpen: false });
      },
      onDropped: () => {
        set({ isOpen: false, flow: null });
      },
    });

    return {
      isOpen: false,
      flow: null,

      openOnboarding: params => {
        // Checked once, here, before anything is written: covers idle, pending AND held
        // re-entry alike, so the store can never hold an invisible grant for a variant with
        // no steps (GlobalOnboarding renders nothing for `steps.length === 0`, and a held
        // grant nobody can see would wedge every other overlay behind it forever).
        // `variants` is a string-keyed Record, so an unknown variant reads undefined at
        // runtime even though the type says otherwise -- cast rather than optional-chain a
        // non-nullable.
        const variant = onboardingFlows[params.flowId].variants[params.variant] as
          OnboardingVariant | undefined;
        if (!variant || variant.steps.length === 0) return;

        // Always write the payload first: a re-entry while pending or held replaces it in
        // place, so a new flow identity is visible immediately even though nothing else
        // happens yet.
        set({ flow: { ...params, actions: params.actions ?? {} } });

        if (flow.getState() === 'idle') {
          flow.request({
            stillNeeded: () => {
              // Re-reads at grant time, so a flow marked complete elsewhere while it waited
              // (e.g. a celebration marking EventGoingWelcome done) is dropped instead of
              // shown.
              const f = get().flow;
              return (
                f !== null &&
                !isCompleted(f.flowId, f.variant as FlowVariantMap[typeof f.flowId], f.contextId)
              );
            },
            onBackButton: () => {
              if (backHandler) backHandler();
              else flow.end('user');
            },
          });
        }
        // Pending or held: the payload was replaced in place above, and nothing else happens.
      },

      setBackHandler: fn => {
        backHandler = fn;
      },

      closeOnboarding: (reason = 'programmatic') => {
        const wasHeld = flow.getState() === 'held';
        flow.end(reason);
        if (!wasHeld) set({ isOpen: false, flow: null });
      },
    };
  },
);
